import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { Banner, Box, Divider, StackBlock } from '@yonderland/andes-react';
import { CollectStoreSelected } from './CollectStoreSelected';
import { useDeliveryState } from '../../../../context/CheckoutDeliveryContext';
import { DeliveryOptionsEnum, LocationSearchType, ProductTypes, StoreFinderType, } from '@as-react/common-types';
import { useAemConfig } from '@as-react/common-components';
import { useTranslation } from 'react-i18next';
import { I18N_NAMESPACES } from '../../../../constants/i18nConstants';
import { makeGetBasketMappedProductsState, makeGetBasketTotalQuantityState, } from '../../../../redux/basket/selectors/BasketSelector';
import { useSelector } from 'react-redux';
import { StoreFinderPopup } from '../../../product-detail/store-finder-popup/StoreFinderPopup';
import { CustomerDetailsFields } from '../CustomerDetailsFields';
import { CustomerDetailsPreview } from '../CustomerDetailsPreview';
import { useQuery } from '@tanstack/react-query';
import { storeQueries } from '@as-react/common-queries';
import { useValidateCustomerDetails } from '../useValidateCustomerDetails';
import { useFormContext } from 'react-hook-form';
export const CollectStoreContainer = () => {
    var _a;
    const { t } = useTranslation([I18N_NAMESPACES.buy, I18N_NAMESPACES.general]);
    const [{ info, errors, activeDeliveryOption, openSelector }] = useDeliveryState();
    const [{ collectStore }, dispatch] = useDeliveryState();
    const { defaultRequestParams, servicesEndpoint, businessUnitId, fictiveWebShop, isShopLanding } = useAemConfig();
    const products = useSelector(makeGetBasketMappedProductsState());
    const productAmount = useSelector(makeGetBasketTotalQuantityState());
    const { selectedStore } = collectStore;
    const isOpen = openSelector === 'store-finder';
    const { store: storeId } = info;
    const [locationSearchParams, setLocationSearchParams] = useState();
    const { formState } = useFormContext();
    const { showCustomerDetailsForm, handleEdit: handleEditCustomerDetails } = useValidateCustomerDetails();
    const hasCustomerError = formState.errors.customer;
    const hasCustomerDetailsError = hasCustomerError &&
        (hasCustomerError.idTitle ||
            hasCustomerError.firstName ||
            hasCustomerError.middleName ||
            hasCustomerError.lastName);
    const showCustomerForm = hasCustomerDetailsError || showCustomerDetailsForm;
    const { getClickAndCollectStoresOpeningsHours, getStoreInformation } = storeQueries({
        servicesEndpoint,
        defaultRequestParams,
    });
    const productData = products
        .filter(product => product.productType === ProductTypes.PRODUCT)
        .map(product => ({
        skuId: Number(product.sku),
        productId: Number(product.productId),
        colorId: Number(product.colorId),
        quantity: product.quantity,
    }));
    const isEnabled = isOpen && locationSearchParams && !!((_a = locationSearchParams.locationSearch) === null || _a === void 0 ? void 0 : _a.location) && productData.length > 0;
    const storesQueryResult = useQuery(getClickAndCollectStoresOpeningsHours({
        businessUnitId,
        fictiveShopId: fictiveWebShop,
        productData,
        location: ((locationSearchParams === null || locationSearchParams === void 0 ? void 0 : locationSearchParams.locationSearch) || {}).location,
        onlyInStock: (locationSearchParams === null || locationSearchParams === void 0 ? void 0 : locationSearchParams.isShowStock) || false,
        onlyInSpecialtyStore: false,
        sortBy: (locationSearchParams === null || locationSearchParams === void 0 ? void 0 : locationSearchParams.sortBy) || 'distance',
        isShopLanding: isShopLanding,
        selectedStoreId: (locationSearchParams === null || locationSearchParams === void 0 ? void 0 : locationSearchParams.locationSearch.type) === LocationSearchType.CUSTOM_SEARCH
            ? undefined
            : locationSearchParams === null || locationSearchParams === void 0 ? void 0 : locationSearchParams.selectedStoreId,
    }, { enabled: Boolean(isEnabled) }));
    const validateStore = activeDeliveryOption && !!storeId && activeDeliveryOption.name === DeliveryOptionsEnum.COLLECT_STORE;
    const { data: storeInformation } = useQuery(getStoreInformation({
        businessUnitId,
        storeId: (selectedStore || {}).storeId || storeId,
        weeks: 2,
    }, {
        enabled: Boolean(validateStore),
    }));
    const { data: storesWithOpeningHours } = useQuery(getClickAndCollectStoresOpeningsHours({
        businessUnitId,
        fictiveShopId: fictiveWebShop,
        productData,
        location: {
            lat: Number(storeInformation === null || storeInformation === void 0 ? void 0 : storeInformation.geographic.latitude),
            lng: Number(storeInformation === null || storeInformation === void 0 ? void 0 : storeInformation.geographic.longitude),
        },
        onlyInStock: false,
        onlyInSpecialtyStore: false,
        sortBy: 'distance',
        isShopLanding: isShopLanding,
        selectedStoreId: storeId,
    }, {
        enabled: Boolean(storeInformation && storeId),
    }));
    useEffect(() => {
        if ((activeDeliveryOption === null || activeDeliveryOption === void 0 ? void 0 : activeDeliveryOption.name) === DeliveryOptionsEnum.COLLECT_STORE && !validateStore) {
            toggleStoreFinder(true);
        }
    }, [activeDeliveryOption, selectedStore]);
    useEffect(() => {
        if (!storeInformation || !storesWithOpeningHours) {
            return;
        }
        const preselectedStore = storesWithOpeningHours === null || storesWithOpeningHours === void 0 ? void 0 : storesWithOpeningHours.find(store => store.storeId === storeId);
        if (preselectedStore) {
            dispatch({
                type: 'select_store',
                payload: Object.assign({}, preselectedStore),
            });
            return;
        }
        toggleStoreFinder(true);
    }, [isOpen, validateStore, storeInformation, storesWithOpeningHours]);
    const toggleStoreFinder = (state) => {
        dispatch({ type: 'set_open_selector', payload: state ? 'store-finder' : undefined });
    };
    useEffect(() => {
        if (!isOpen && !selectedStore) {
            dispatch({ type: 'set_active_delivery_option', payload: undefined });
        }
    }, [isOpen]);
    const handleSearch = (searchParams) => {
        setLocationSearchParams(searchParams);
    };
    const handleStoreSelect = (store) => {
        if (store.storeId === (selectedStore === null || selectedStore === void 0 ? void 0 : selectedStore.storeId)) {
            return;
        }
        dispatch({ type: 'select_store', payload: store });
        toggleStoreFinder(false);
    };
    const resetCollectStore = () => {
        toggleStoreFinder(false);
    };
    return activeDeliveryOption && activeDeliveryOption.name === DeliveryOptionsEnum.COLLECT_STORE ? (_jsxs(_Fragment, { children: [selectedStore && (_jsxs(StackBlock, { gap: "2", children: [_jsx(Box, { paddingBlock: "2", children: _jsxs(StackBlock, { gap: showCustomerForm ? '4' : undefined, children: [_jsx(CollectStoreSelected, {}), _jsx(Divider, {}), showCustomerForm ? (_jsx(CustomerDetailsFields, { prefix: "customer." })) : (_jsx(CustomerDetailsPreview, { onEdit: handleEditCustomerDetails }))] }) }), activeDeliveryOption && errors[activeDeliveryOption.name] && (_jsx(Banner, { text: t(errors[activeDeliveryOption.name]), status: "error" }))] })), _jsx(StoreFinderPopup, { type: StoreFinderType.CHECKOUT, title: t('checkout:checkout.delivery.store.flyout.title'), isOpen: isOpen, onSearch: handleSearch, textCTA: t('checkout:checkout.delivery.store.flyout.cta', {
                    count: productAmount,
                }), handleClosePopup: resetCollectStore, onStoreSelect: handleStoreSelect, storesQueryResult: storesQueryResult })] })) : null;
};
